/* eslint-disable @nx/enforce-module-boundaries */
import React, { useEffect, useState } from 'react';
import { Box, Flex, HStack, Stack, VStack } from '@mybridge/ui/layout';
import {
  CheckAll,
  AddPostIcon,
  ArticleIcon,
  CalendarIcon,
  GroupIcon,
  JobsIcon,
  PageIcon,
  PagesIcon,
  EyeViewFilledIcon,
} from '@mybridge/icons';
import { List, ListItem } from '@mybridge/ui/list';
import { Text } from '@mybridge/ui/text';
import { useSelector } from 'react-redux';
import {
  SettingsDiskSubscriptionIcon,
  SettingsIntegrationIcon,
  SettingsNotificationsIcon,
  SettingPageIcon,
  SettingsPaymentsIcon,
  SettingsTeamsIcon,
  SettingHelpIcon,
  SettingPrivacyIcon,
} from '@mybridge/icons';
import { getUserFullName, getUserProfilePic } from 'v4/lib/commons';
import { Avatar } from '@mybridge/ui';
import { isMobile } from 'react-device-detect';
import { useRouter } from 'next/router';
import { SiteShortcutLinks } from 'v4/snippets/dashboard/site-shortcut-links';
import { RightArrow } from '@mybridge/icons/CampaignIcon';
import { DashboardMyCircle } from 'v4/snippets/dashboard/circle';
import { VisibilityAudienceIcon } from '@mybridge/icons/VisibilityAudienceIcon';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';

const Sidebar = ({
  heading,
  selectedComponent,
  setSelectedComponent,
  setAllFiltersQuery,
  setFilterValuesState,
  settingsPage,
  isSearch,
  selectedSubItem,
  setSelectedSubItem,
}) => {
  const router = useRouter();
  const { pathname } = router;
  // const routers = useRouter();
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  const userFullName = getUserFullName(userProfileInfo);
  const userProfilePic = getUserProfilePic(userProfileInfo);
  const isSettingPage = pathname?.includes('settings');
  const slug = router?.query?.slug?.toString();
  const { t } = useTranslator();

  // Add state to track the dropdown state from SiteShortcutLinks
  const [moreDropdownOpen, setMoreDropdownOpen] = useState(false);
  
  // Callback function to receive state from SiteShortcutLinks
  const handleSettingPageChange = (isOpen) => {
    setMoreDropdownOpen(isOpen);
  };

  // State for dropdown
  const [integrationOpen, setIntegrationOpen] = useState(false);

  // State for submenu selection

  const settings = [
    {
      name: t('setting.account') || 'account',
      icon: (
        <Avatar
          name={userFullName}
          src={userProfilePic}
          width="30px"
          height="30px"
        />
      ),
      link: 'account',
      showOnMobile: true,
    },
    {
      name: t('setting.notifications') || 'notifications',
      icon: <SettingsNotificationsIcon />,
      link: 'notifications',
      showOnMobile: true,
    },
    {
      name: t('setting.pages') || 'pages',
      icon: <SettingPageIcon />,
      link: 'pages',
      showOnMobile: true,
    },
    {
      name: isMobile ?  t('setting.subscriptionsAndPayment') || 'Subscriptions & Payment': t('setting.billingAndPayment') || 'Billing and payment',
      icon: <SettingsPaymentsIcon />,
      link: 'billing-and-payment',
      showOnMobile: false,
    },
    {
      name: t('setting.permissions') || 'permissions',
      icon: <SettingsTeamsIcon />,
      link: 'permissions',
      showOnMobile: false,
    },
    ...(isMobile ? [] : [{
      name: t('setting.subscriptions') || 'subscriptions',
      icon: <SettingsDiskSubscriptionIcon />,
      link: 'subscriptions',
      showOnMobile: false,
    }]),
    {
      name: t('setting.integration') || 'integration',
      icon: <SettingsIntegrationIcon />,
      link: 'integration',
      showOnMobile: false,
      submenu: ['calendar sync', 'Video conferencing'],
    },
    // Conditionally add the following object if not mobile
    ...([{
      name: t('setting.audienceAndVisibility') || 'Audience and visibility',
      icon: <VisibilityAudienceIcon color="#5B5B5B" />,
      link: 'audience-and-visibility',
      showOnMobile: false,
    }]),
    ...(!isMobile ? [] : [{
      name: t('setting.helpAndSupport') || 'Help and support',
      icon: <SettingHelpIcon color="#5B5B5B" />,
      link: 'support',
      showOnMobile: false,
    }]),
    ...(!isMobile ? [] : [{
      name: t('setting.privacyAndTerms') || 'Privacy and Terms',
      icon: <SettingPrivacyIcon color="#5B5B5B" />,
      link: 'privacy-policy',
      showOnMobile: false,
    }]),
];

const submenuItems = [
  {
    key: 'calendar sync',
    translationKey: 'setting.calendarSync'
  },
  {
    key: 'video conferencing',
    translationKey: 'setting.videoConferencing'
  }
];
  const coreModules = [
    { name: 'all', icon: <CheckAll />, link: 'all' },
    { name: 'posts', icon: <AddPostIcon />, link: 'posts' },
    {
      name: 'people',
      icon: <GroupIcon width={15} height={15} />,
      link: 'people',
    },
    { name: 'jobs', icon: <JobsIcon width={15} height={15} />, link: 'jobs' },
    { name: 'pages', icon: <PageIcon width={15} height={15} />, link: 'pages' },
    { name: 'team', icon: <GroupIcon height={15} />, link: 'team' },
    { name: 'events', icon: <CalendarIcon height={15} />, link: 'events' },
    { name: 'articles', icon: <ArticleIcon />, link: 'articles' },
    {
      name: 'integration',
      icon: <SettingsIntegrationIcon height={15} />,
      link: 'integration',
    },
  ];

  const items = settingsPage ? settings : coreModules;
  const handleIntegrationClick = () => {
    router?.push(`/settings/integration`);
    setIntegrationOpen((prev) => !prev);
    !isMobile ? setSelectedComponent('calendar-sync'):setSelectedComponent('integration')
    setSelectedSubItem('calendar-sync'); // Default to 'Calendar sync'
  };

  useEffect(() => {
    if (!router.isReady) return;
  
    const { title } = router.query;
  
    if (title) {
      setSelectedComponent('video-conferencing');
      setSelectedSubItem('video-conferencing');
    }
  
  }, [router.isReady, router.query]);

  useEffect(() => {
    if (
      slug?.includes('integration') ||
      router?.query?.slug?.includes('zoom') ||
      router?.query?.slug?.includes('teams') ||
      router?.query?.slug?.includes('webex') ||
      router?.query?.slug?.includes('gotomymeeting')
   
    ) {
      setIntegrationOpen(true);
       
      if (slug?.includes('conferencing')) {
        setSelectedComponent('video-conferencing');
        setSelectedSubItem('video-conferencing');
      }
      if(slug?.includes('integration')){
      isMobile && setSelectedComponent('integration');
      }
    }
  }, [slug]);

  return (
    <>
      {/* Keep SiteShortcutLinks Always Visible */}
      {!isMobile && isSettingPage && (
        <>
         <HStack pos="sticky" top="78px"  height={'420px'} alignItems="stretch" spacing={0}>
        <Stack
          display={['none', 'none', 'flex']}
          pos="sticky"
          top="64px"
          spacing={2}
          minH="100vh"
          borderBottom="1px solid #e0e0e094"
          height={'500px'}
          zIndex="999"
        >
          <VStack
            spacing={2}
            justifyContent="space-between"
            height="100%"
            align="stretch"
            h="calc(100vh - 0px)"
            backgroundColor="rgb(255, 255, 255)"
          >
            <VStack spacing={2} align="stretch" h="100%" flex="1"  w={!isMobile && "min-content"}>
              <SiteShortcutLinks onSettingPageChange={handleSettingPageChange} />
              <Box
              //  className={styles.hideTitle}
              >
                <DashboardMyCircle h="300px" flex="1" display={['none', 'none', 'none', 'flex']}/>
              </Box>
            </VStack>
          </VStack>
        </Stack>
      </HStack>
    </>  )}
      <List
        pos={!isMobile && !moreDropdownOpen ? "sticky" : 'initial'}
        {...(moreDropdownOpen && {zIndex: 'auto'})}
        top={!isMobile && "78px"}
        h={!isMobile && "420px"}
        textTransform="capitalize"
        sx={{
          width: ['100%','300px', '200px', '300px'],
          // '@media (max-width: 1280px)': {
          //   width: '149px', // Specifically for 1280px and above
          // },
        }}
        p={['10px', '0']}
        flexShrink="0"
        boxShadow="none"
      >
        {/* {heading ?? (
        <Text color="brandPrimary.500" fontWeight="400" ps="10px">
          {settingsPage ? 'Settings' : 'Search Results'}
        </Text>
      )} */}
        {items?.map((item, i) => (
          <React.Fragment key={i}>
            <ListItem
              onClick={() => {
                if (item.name === t('setting.integration')) {
                  handleIntegrationClick();
                } else {
                  setIntegrationOpen(false);
                  setSelectedComponent(item.link);
                      router.push(`/settings/${item?.link}`);

                  {
                    isSearch || (!settingsPage && setAllFiltersQuery([]));
                  }
                }
              }}
              my={['1%', '10px']}
              borderRadius={['30px', '50px']}
              border={['1px solid #D9D9D9', 'none']}
              userSelect="none"
              color="brandGray.750"
              boxShadow="none"
              bgColor={[
                'none',
                selectedComponent === item.link ||
                selectedComponent?.toLowerCase()?.replace('-', ' ') ===
                  item?.submenu?.[0] ||
                selectedComponent?.includes(item?.submenu?.[1]?.slice(6))
                  ? 'brandGray.500'
                  : '',
              ]}
              sx={{
                ':hover': {
                  backgroundColor: 'brandGray.500',
                  cursor: 'pointer',
                  path: {
                    fill: 'white',
                  },
                  '.circleIcon': {
                    bgColor: 'brandPrimary.500',
                  },
                  circle: {
                    fill: 'brandPrimary.500',
                  },
                },
              }}
            >
              <Flex gap="2" alignItems="center" p="10px" width="100%">
                {(!isSettingPage || (isSettingPage && isMobile)) && (
                  <Box
                    position="relative"
                    w="30px"
                    h="30px"
                    bgColor={['none',
                      selectedComponent === item.link
                        ? 'brandPrimary.500'
                        : 'brandGray.500']
                    }
                    borderRadius="50%"
                    className="circleIcon"
                    sx={
                      (selectedComponent === item.link ||
                        selectedComponent?.includes(
                          item?.submenu?.[1]?.slice(6)
                        ) ||
                        selectedComponent?.toLowerCase()?.replace('-', ' ') ===
                          item?.submenu?.[0]) && {
                        circle: {
                          fill: 'brandPrimary.500',
                        },
                      }
                    }
                  >
                    <Box
                      position="absolute"
                      left="50%"
                      top="50%"
                      transform="translate(-50%, -50%)"
                      sx={
                        ['none',(selectedComponent === item.link ||
                          selectedComponent?.includes(
                            item?.submenu?.[1]?.slice(6)
                          ) ||
                          selectedComponent
                            ?.toLowerCase()
                            ?.replace('-', ' ') === item?.submenu?.[0]) && {
                          path: {
                            fill: 'white',
                          },
                        }]
                      }
                    >
                      {item.icon}
                    </Box>
                  </Box>
                )}
                <Text>{item.name}</Text>
                {item.name === t('setting.integration') && (
                  <Flex
                    display="flex"
                    width="40%"
                    justifyContent="end"
                    sx={{
                      ':hover': {
                        backgroundColor: 'brandGray.500',
                        cursor: 'pointer',
                        path: {
                          fill: 'none',
                        },
                        '.circleIcon': {
                          bgColor: 'brandPrimary.500',
                        },
                        circle: {
                          fill: 'brandPrimary.500',
                        },
                      },
                    }}
                  >
                    {!isMobile && (integrationOpen ? (
                      <img src="/upArrow.png" alt="" />
                    ) : (
                      // <ArrowUpFilledIcon width={"16.5px"} height={"12px"} ml="8px" fill="none !important" /> // Show up arrow when open
                      <img src="/downArrow.png" alt="" />
                      // <ArrowDownFilledIcon width={"16.5px"} height={"12px"} ml="8px" fill="none !important" /> // Show down arrow when closed
                    ))}
                  </Flex>
                )}
                {isMobile && <Box ml="auto">
                  <RightArrow />
                </Box>}
              </Flex>
            </ListItem>
            {/* Submenu for Integration */}
            {item.name === t('setting.integration') && integrationOpen && (
              <Box pl="20px">
                {/* {item.submenu.map((subItem, subIndex) => (
                  <ListItem
                    key={subIndex}
                    onClick={() => {
                      const componentKey = subItem
                        .toLowerCase()
                        .replace(' ', '-');
                      setSelectedSubItem(componentKey);

                      // Add routing logic for the new submenu item
                      if (componentKey === 'calendar-sync') {
                        router?.push(`/settings/integration`);
                      }else if(isMobile ){
                        router?.push(`/settings/integration`);
                      } else {
                        router?.push('/settings/integration/conferencing');
                      }
                    }}
                    my={6}
                    ml={4}
                    borderRadius="50px"
                    cursor="pointer"
                    userSelect="none"
                    color={
                      selectedSubItem ===
                      subItem.toLowerCase().replace(' ', '-')
                        ? '#3D5A80'
                        : ''
                    }
                    sx={{
                      ':hover': {
                        color: '#3D5A80',
                      },
                    }}
                  >
                    {subItem && t(`setting.${subItem.toLowerCase().replace(' ', '')}`) || subItem}
                  </ListItem>
                ))} */}
                {item.submenu.map((subItem, subIndex) => {
                  const menuItem = submenuItems.find(item => 
                    item.key.toLowerCase() === subItem.toLowerCase()
                  );
                  
                  return (
                    <ListItem
                      key={subIndex}
                      onClick={() => {
                        const componentKey = subItem.toLowerCase().replace(' ', '-');
                        setSelectedSubItem(componentKey);
                        
                        if (componentKey === 'calendar-sync') {
                          router?.push(`/settings/integration`);
                        } else if (isMobile) {
                          router?.push(`/settings/integration`);
                        } else {
                          router?.push('/settings/integration');
          // router?.push('/settings/integration/conferencing');

                        }
                      }}
                      my={6}
                      ml={4}
                      borderRadius="50px"
                      cursor="pointer"
                      userSelect="none"
                      color={
                        selectedSubItem ===
                        subItem.toLowerCase().replace(' ', '-')
                          ? '#3D5A80'
                          : ''
                      }
                      sx={{
                        ':hover': {
                          color: '#3D5A80',
                        },
                      }}
                    >
                      {t(menuItem.translationKey) || subItem}
                    </ListItem>
                  );
                })}
              </Box>
            )}
          </React.Fragment>
        ))}
      </List>
      
    </>
  );
};

export default Sidebar;
